import { SavedFilter } from 'types/PropertyFilters';
import { DEFAULT_FILTER } from 'util/Constants';
import { getFiltersFromLocalStorage, saveFiltersToLocalStorage } from 'util/localStorageHelper';
import { FilterType, LocalSavedFilter } from 'util/Types';
import { validateAndCleanFilter } from 'util/filterValidation';
import { create } from 'zustand';

const CURRENT_FILTER_KEY = 'currentFilter';

const convertToSavedFilter = (filter: LocalSavedFilter): SavedFilter => {
    return {
        id: filter.id,
        title: filter.title,
        filter: {
            country: filter.filter.country,
            city: filter.filter.city,
            state: filter.filter.state,
            type: filter.filter.type,
            locations: filter.filter.locations,
            price: {
                from: filter.filter.price.from,
                to: filter.filter.price.to,
            },
            constructionArea: {
                from: filter.filter.constructionArea.from,
                to: filter.filter.constructionArea.to,
            },
            plotArea: {
                from: filter.filter.plotArea.from,
                to: filter.filter.plotArea.to,
            },
            pricePerSquareMeter: {
                from: filter.filter.pricePerSquareMeter.from,
                to: filter.filter.pricePerSquareMeter.to,
            },
            bedbath: filter.filter.bedbath,
            listingType: filter.filter.listingType,
            neighborhood: filter.filter.neighborhood,
            availability: filter.filter.availability || ['available'],
        },
    };
};

interface FilterStore {
    savedFilterList: SavedFilter[];
    currentFilter: FilterType;

    setCurrentFilter: (filter: FilterType) => void;

    addFilter: (filter: LocalSavedFilter | SavedFilter) => void;
    removeFilter: (idx: number) => void;
    updateFilter: (idx: number, filter: LocalSavedFilter | SavedFilter) => void;
    validateAllFilters: () => { validFilters: SavedFilter[]; removedFilters: string[] };
}

export const useFiltersStore = create<FilterStore>((set, get) => {
    // Initialize filters on store creation (no validation on startup)
    const initialFilters = getFiltersFromLocalStorage() || [];

    return {
        savedFilterList: initialFilters,
        currentFilter: (() => {
            const savedFilter = localStorage.getItem(CURRENT_FILTER_KEY);
            const filter = savedFilter ? JSON.parse(savedFilter) : DEFAULT_FILTER;

            // Ensure the filter has proper availability field
            return {
                ...filter,
                availability: filter.availability || ['available'],
            };
        })(),

        setCurrentFilter: (filter) => {
            // Ensure the filter has proper availability field
            const normalizedFilter = {
                ...filter,
                availability: filter.availability || ['available'],
            };

            localStorage.setItem(CURRENT_FILTER_KEY, JSON.stringify(normalizedFilter));
            set(() => ({ currentFilter: normalizedFilter }));
        },

        addFilter: (filter) => {
            const savedFilter = 'filter' in filter && 'type' in filter.filter ? convertToSavedFilter(filter as LocalSavedFilter) : (filter as SavedFilter);

            // Ensure the saved filter has proper availability field
            if (!savedFilter.filter.availability) {
                savedFilter.filter.availability = ['available'];
            }

            const newFilterList = [...get().savedFilterList, savedFilter];
            set(() => ({ savedFilterList: newFilterList }));
            saveFiltersToLocalStorage(newFilterList);
        },

        removeFilter: (idx) => {
            const newFilterList = [...get().savedFilterList.slice(0, idx), ...get().savedFilterList.slice(idx + 1)];
            set(() => ({ savedFilterList: newFilterList }));
            saveFiltersToLocalStorage(newFilterList);
        },

        validateAllFilters: () => {
            // This method is kept for backward compatibility but no longer performs validation
            const currentFilters = get().savedFilterList;
            return { validFilters: currentFilters, removedFilters: [] };
        },

        updateFilter: (idx, filter) => {
            const savedFilter = 'filter' in filter && 'type' in filter.filter ? convertToSavedFilter(filter as LocalSavedFilter) : (filter as SavedFilter);

            // Ensure the saved filter has proper availability field
            if (!savedFilter.filter.availability) {
                savedFilter.filter.availability = ['available'];
            }

            const newFilterList = get().savedFilterList;
            newFilterList[idx] = savedFilter;
            set(() => ({ savedFilterList: newFilterList }));
            saveFiltersToLocalStorage(newFilterList);
        },
    };
});
